import React, { useState, useEffect } from 'react';
import api from '../../config/axios';
import { groupTax, groupTotal } from '../../utils/tax';
import { useNavigate, useLocation } from 'react-router-dom';
import '../order/OrderPayment.css';

interface GroupOrder {
  sessionCode: string;
  tableId: string;
  tableName: string;
  orders: Array<{
    orderId: string;
    customerName: string;
    customerPhone: string;
    items: Array<{
      name: string;
      price: number;
      quantity: number;
    }>;
    subtotal: number;
    createdAt: string;
  }>;
  totalAmount: number;
  expiresAt: string;
}

const GroupOrderPayment: React.FC = () => {
  const [groupOrder, setGroupOrder] = useState<GroupOrder | null>(null);
  const [paymentMethod, setPaymentMethod] = useState('momo');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionCode = params.get('sessionCode');
    
    if (sessionCode) {
      fetchGroupSession(sessionCode);
    }
  }, [location.search]);

  const fetchGroupSession = async (sessionCode: string) => {
    try {
      const res = await api.get(`/api/group-orders/${sessionCode}`);
      setGroupOrder(res.data.data);
    } catch (err: any) {
      setError('Không thể tải thông tin nhóm');
    }
  };

  const handlePayment = async () => {
    if (!groupOrder) {
      setError('Không có thông tin nhóm');
      return;
    }

    const totalWithTax = groupTotal(groupOrder);

    try {
      setLoading(true);
      setError('');

      if (paymentMethod === 'momo') {
        const res = await api.post('/api/group-orders/pay', {
          sessionCode: groupOrder.sessionCode,
          paymentMethod: 'momo',
          amountPaid: Math.round(totalWithTax)
        });

        if (res.data.success && res.data.data.payUrl) {
          window.location.href = res.data.data.payUrl;
        } else {
          setError('Không thể tạo link thanh toán MoMo');
        }
      }

    } catch (err: any) {
      setError(err.response?.data?.message || 'Lỗi khi thanh toán');
    } finally {
      setLoading(false);
    }
  };

  if (!groupOrder) {
    return (
      <div className="order-payment-bg">
        <div className="order-payment-container">
          <div className="order-payment-loading">Đang tải thông tin...</div>
        </div>
      </div>
    );
  }

  // Thuế của nhóm = tổng thuế từng món của mọi đơn trong phiên.
  const taxAmount = Math.round(groupTax(groupOrder));
  const totalWithTax = Math.round(groupTotal(groupOrder));

  return (
    <div className="order-payment-bg">
      <div className="order-payment-container">
        <h2 className="order-payment-title">THANH TOÁN NHÓM</h2>
        
        <div style={{ 
          background: '#f0f9ff', 
          border: '1px solid #0ea5e9', 
          borderRadius: 8, 
          padding: 16, 
          marginBottom: 24 
        }}>
          <div style={{ marginBottom: 8 }}>
            <strong>Session Code: {groupOrder.sessionCode}</strong>
          </div>
          <div>Bàn: {groupOrder.tableName}</div>
          <div>Số người: {groupOrder.orders.length}</div>
        </div>

        <div style={{ marginBottom: 24 }}>
          <h3 style={{ marginBottom: 16, color: '#333' }}>Chi tiết đơn hàng:</h3>
          {groupOrder.orders.map((order, index) => (
            <div key={order.orderId} style={{ 
              border: '1px solid #e2e8f0', 
              borderRadius: 8, 
              padding: 12, 
              marginBottom: 12,
              background: '#f8fafc'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <strong>{order.customerName}</strong>
                <span style={{ color: '#059669', fontWeight: 'bold' }}>
                  {order.subtotal.toLocaleString()} đ
                </span>
              </div>
              <div style={{ fontSize: '12px', color: '#64748b', marginBottom: 8 }}>
                {order.customerPhone}
              </div>
              <div style={{ fontSize: '14px' }}>
                {order.items.map((item, idx) => (
                  <span key={idx} style={{ marginRight: 12 }}>
                    {item.name} x{item.quantity}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div style={{ 
          background: '#f0fdf4', 
          border: '1px solid #22c55e', 
          borderRadius: 8, 
          padding: 16, 
          marginBottom: 24 
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <span>Tổng tiền món:</span>
            <span>{groupOrder.totalAmount.toLocaleString()} đ</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <span>Thuế:</span>
            <span>{taxAmount.toLocaleString()} đ</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', fontSize: '18px', color: '#059669' }}>
            <span>Tổng cộng:</span>
            <span>{totalWithTax.toLocaleString()} đ</span>
          </div>
        </div>

        {error && <div className="order-payment-message order-payment-error">{error}</div>}
        {success && <div className="order-payment-message order-payment-success">{success}</div>}

        <div style={{ marginBottom: 24 }}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold', color: '#333' }}>
              Phương thức thanh toán:
            </label>
            <div style={{ 
              padding: '12px', 
              fontSize: '16px',
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              color: '#64748b'
            }}>
              MoMo
            </div>
          </div>
        </div>

        <button 
          onClick={handlePayment} 
          className="order-payment-submit-btn" 
          disabled={loading}
          style={{ width: '100%', fontSize: '16px', padding: '16px' }}
        >
          {loading ? 'Đang xử lý...' : 'Thanh Toán MoMo'}
        </button>

        <button 
          onClick={() => navigate(`/orders/group-order?sessionCode=${groupOrder.sessionCode}`)}
          className="order-payment-cancel-btn"
          style={{ width: '100%', marginTop: 12, fontSize: '16px', padding: '16px' }}
        >
          Quay Lại
        </button>
      </div>
    </div>
  );
};

export default GroupOrderPayment; 